// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 4, Iter 332).
// Verkapselt alle Slot-Klick-Aktionen:
//   - handleSlotClick     → öffnet Booking-Dialog inkl. Gap-Check und
//                            Mindbody-Auto-Select wenn der Slot neben
//                            einem gemappten MB-Slot liegt.
//   - handleUnavailableSlotClick → öffnet Availability-Dialog für gesperrte
//                                  Slots.
//   - handleAvailableSlotClick   → öffnet Availability-Dialog mit „blockieren"
//                                  Option für freie Slots.
//   - handleAddException  → API-Call: macht einen geblockten Slot offen.
//   - handleAddBlock      → API-Call: blockt einen Zeitraum (löscht
//                            vorhandene Exceptions in der Range).
//
// Verhalten ist identisch zum vorherigen Inline-Code.

import axios from 'axios';
import { toast } from 'sonner';
import { getSwissHM, toLocalYMD } from '@/components/appointments/calendarUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useSlotClickActions = ({
  // Daten
  services,
  customers,
  currentDate,
  trainerExceptions,
  trainerBlocks = [],
  studioBlocks = [],
  studioFilter,
  // Memo-Helpers
  minServiceTotalDuration,
  getMaxGapMinutes,
  findMindbodyMappedSlotAt,
  // Setter Booking-Dialog
  setSelectedSlot,
  setSelectedTrainer,
  setBookingData,
  setCustomTime,
  setBookingDialogOpen,
  // Setter Availability-Dialog
  setAvailabilitySlot,
  setAvailabilityDialogOpen,
  availabilitySlot,
  // Fetcher (kommen aus useCalendarData)
  fetchServices,
  fetchCustomers,
  fetchAvailableServices,
  fetchTrainers,
  fetchVacationsAndBlocks,
  fetchStudioBlocks,
}) => {
  const handleSlotClick = async (trainer, timeSlot) => {
    setSelectedSlot(timeSlot);
    setCustomTime(timeSlot);
    setSelectedTrainer(trainer);
    setBookingData({ service_id: '', customer_id: '', notes: '' });

    // Load services and customers if not already loaded
    if (services.length === 0) await fetchServices();
    if (customers.length === 0) fetchCustomers();

    const [hours, minutes] = timeSlot.split(':');
    const date = toLocalYMD(currentDate);
    const time = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    const trainerId = trainer.trainer?.id || trainer.id;

    // 08/2026 Bug-Fix: Der clientseitige Gap-Vor-Filter (Iter 318) lieferte
    // falsche "Nur 0 Min. frei"-Blockaden (z.B. bei sekundengenauen oder
    // gespiegelten Terminen) und verhinderte Buchungen auf sichtbar freien
    // Slots. Jetzt ist das Backend (/available-services) die Source of
    // Truth: Liefert es 0 passende Services, öffnen wir den Dialog nicht
    // (Warn-Toast kommt bereits aus fetchAvailableServices).
    const _mbAtClick = findMindbodyMappedSlotAt(trainerId, timeSlot);
    const _svcData = await fetchAvailableServices(trainerId, date, time);
    if (
      !_mbAtClick &&
      _svcData &&
      !_svcData.all_services_allowed &&
      (_svcData.services || []).length === 0
    ) {
      return;
    }

    // Iter 324: Auto-Select des gemappten Service falls Klick neben
    // einem BELEGT-Mindbody-Slot mit Service-Mapping.
    if (_mbAtClick && _mbAtClick.strongfit_service_id) {
      try {
        const mbHM = getSwissHM(_mbAtClick.start_time);
        const mbHHMM = `${String(mbHM.hour).padStart(2, '0')}:${String(mbHM.minute).padStart(2, '0')}`;
        setCustomTime(mbHHMM);
        setSelectedSlot(mbHHMM);
        if (mbHHMM !== time) {
          await fetchAvailableServices(trainerId, date, mbHHMM);
        }
      } catch {
        /* best-effort */
      }
      setBookingData((prev) => ({
        ...prev,
        service_id: _mbAtClick.strongfit_service_id,
      }));
      toast.info(
        `Service vorausgewählt: ${_mbAtClick.service_name || 'Mindbody-Gruppenslot'}`,
        { duration: 2500 }
      );
    }

    setBookingDialogOpen(true);
  };

  const handleUnavailableSlotClick = (trainer, timeSlot) => {
    const trainerId = trainer.trainer?.id || trainer.id;
    const dateStr = toLocalYMD(currentDate);
    const [h, m] = timeSlot.split(':').map(Number);
    const t = h * 60 + m;
    // 08/2026 Bug-Fix: Ermittle ob der Slot durch einen MANUELLEN Block
    // gesperrt ist. Wenn ja → im Dialog „Blockierung aufheben" anbieten
    // (löscht den Block sauber) statt eine Ausnahme darüberzulegen, die
    // den Bereich schraffiert „per Ausnahme geöffnet" hinterlässt.
    const blockedByBlock = trainerBlocks.find((b) => {
      if (b.trainer_id !== trainerId || b.date !== dateStr) return false;
      if (b.all_day) return true;
      const [sH, sM] = (b.start_time || '00:00').split(':').map(Number);
      const [eH, eM] = (b.end_time || '23:59').split(':').map(Number);
      return t >= sH * 60 + sM && t < eH * 60 + eM;
    });
    // 06/2026: Ist der Slot durch eine STUDIOWEITE Sperre betroffen?
    const tStudios = [
      ...((trainer.trainer?.studio_ids) || trainer.studio_ids || []),
      trainer.trainer?.studio_id || trainer.studio_id,
    ].filter(Boolean);
    const studioBlockedBy = (studioBlocks || []).find((b) => {
      if (b.date !== dateStr) return false;
      const applies =
        (studioFilter && studioFilter !== 'all' && b.studio_id === studioFilter) ||
        tStudios.includes(b.studio_id);
      if (!applies) return false;
      if (b.all_day) return true;
      const [sH, sM] = (b.start_time || '00:00').split(':').map(Number);
      const [eH, eM] = (b.end_time || '23:59').split(':').map(Number);
      return t >= sH * 60 + sM && t < eH * 60 + eM;
    });
    setAvailabilitySlot({
      trainer,
      timeSlot,
      trainerId,
      trainerName: `${trainer.trainer?.first_name || trainer.first_name} ${trainer.trainer?.last_name || trainer.last_name}`,
      date: dateStr,
      isAvailable: false,
      blockedByBlock: blockedByBlock || null,
      studioBlockedBy: studioBlockedBy || null,
    });
    setAvailabilityDialogOpen(true);
  };

  const handleAvailableSlotClick = (trainer, timeSlot, exceptionOpen = null) => {
    setAvailabilitySlot({
      trainer,
      timeSlot,
      trainerId: trainer.trainer?.id || trainer.id,
      trainerName: `${trainer.trainer?.first_name || trainer.first_name} ${trainer.trainer?.last_name || trainer.last_name}`,
      date: toLocalYMD(currentDate),
      isAvailable: true,
      exceptionOpen,
    });
    setAvailabilityDialogOpen(true);
  };

  // 08/2026: Blocker + zugehörige Ausnahme(n) in einem Schritt entfernen
  // (Aufräumen der schraffierten „per Ausnahme geöffnet"-Bereiche).
  const handleRemoveBlockException = async () => {
    if (!availabilitySlot?.exceptionOpen) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const { block } = availabilitySlot.exceptionOpen;

      const overlappingExceptions = trainerExceptions.filter(
        (exc) =>
          exc.trainer_id === availabilitySlot.trainerId &&
          exc.date === availabilitySlot.date &&
          (exc.start_time || '00:00') < (block.end_time || '23:59') &&
          (exc.end_time || '23:59') > (block.start_time || '00:00')
      );
      for (const exc of overlappingExceptions) {
        await axios.delete(`${API}/trainers/exceptions/${exc.id}`, { headers });
      }
      if (block.id) {
        await axios.delete(`${API}/trainers/blocks/${block.id}`, { headers });
      }

      toast.success('🗑️ Blocker & Ausnahme entfernt — Zeitraum folgt wieder dem regulären Plan.');
      setAvailabilityDialogOpen(false);
      await fetchTrainers();
      await fetchVacationsAndBlocks();
    } catch (error) {
      console.error('Error removing block/exception:', error);
      toast.error('Fehler beim Entfernen von Blocker/Ausnahme');
    }
  };

  // 08/2026: Einen manuellen Block direkt aufheben (Slot wieder freigeben).
  // Löscht ALLE Blocks des Trainers am Tag, die den Slot überlappen, plus
  // eventuell darüberliegende Ausnahmen — der Zeitraum folgt danach wieder
  // dem regulären Verfügbarkeitsplan (keine Schraffur mehr).
  const handleRemoveBlock = async () => {
    if (!availabilitySlot?.blockedByBlock) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const { trainerId, date } = availabilitySlot;
      const [h, m] = availabilitySlot.timeSlot.split(':').map(Number);
      const t = h * 60 + m;

      const overlappingBlocks = trainerBlocks.filter((b) => {
        if (b.trainer_id !== trainerId || b.date !== date) return false;
        const [sH, sM] = (b.start_time || '00:00').split(':').map(Number);
        const [eH, eM] = (b.end_time || '23:59').split(':').map(Number);
        return t >= sH * 60 + sM && t < eH * 60 + eM;
      });

      for (const b of overlappingBlocks) {
        // Ausnahmen die in diesem Block-Zeitraum liegen ebenfalls entfernen
        const bStart = (b.start_time || '00:00');
        const bEnd = (b.end_time || '23:59');
        const overlappingExceptions = trainerExceptions.filter(
          (exc) =>
            exc.trainer_id === trainerId &&
            exc.date === date &&
            (exc.start_time || '00:00') < bEnd &&
            (exc.end_time || '23:59') > bStart
        );
        for (const exc of overlappingExceptions) {
          await axios.delete(`${API}/trainers/exceptions/${exc.id}`, { headers });
        }
        if (b.id) {
          await axios.delete(`${API}/trainers/blocks/${b.id}`, { headers });
        }
      }

      toast.success('✅ Blockierung aufgehoben — Slot ist wieder verfügbar.');
      setAvailabilityDialogOpen(false);
      await fetchTrainers();
      await fetchVacationsAndBlocks();
    } catch (error) {
      console.error('Error removing block:', error);
      toast.error('Fehler beim Aufheben der Blockierung');
    }
  };

  const handleAddException = async () => {
    if (!availabilitySlot) return;
    try {
      const token = localStorage.getItem('token');
      // Calculate end time (30 minutes after start)
      const [hours, minutes] = availabilitySlot.timeSlot.split(':').map(Number);
      const endHour = minutes + 30 >= 60 ? hours + 1 : hours;
      const endMinutes = (minutes + 30) % 60;
      const endTime = `${endHour.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

      await axios.post(
        `${API}/trainers/${availabilitySlot.trainerId}/exceptions`,
        {
          trainer_id: availabilitySlot.trainerId,
          date: availabilitySlot.date,
          start_time: availabilitySlot.timeSlot,
          end_time: endTime,
          reason: 'Manuell geöffnet',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('✅ Slot geöffnet! Dieser Zeitraum ist jetzt buchbar.');
      setAvailabilityDialogOpen(false);
      await fetchTrainers();
      await fetchVacationsAndBlocks();
    } catch (error) {
      console.error('Error adding exception:', error);
      toast.error('Fehler beim Öffnen des Slots');
    }
  };

  const handleAddBlock = async (endTime, reason) => {
    if (!availabilitySlot) return;
    try {
      const token = localStorage.getItem('token');

      const [startH, startM] = availabilitySlot.timeSlot.split(':').map(Number);
      const [endH, endM] = endTime.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;

      // FIRST: Find and delete ALL exceptions within the block range
      const exceptionsToDelete = trainerExceptions.filter((exc) => {
        if (
          exc.trainer_id !== availabilitySlot.trainerId ||
          exc.date !== availabilitySlot.date
        ) {
          return false;
        }
        const [excH, excM] = (exc.start_time || '00:00').split(':').map(Number);
        const excMinutesValue = excH * 60 + excM;
        return excMinutesValue >= startMinutes && excMinutesValue < endMinutes;
      });

      for (const exc of exceptionsToDelete) {
        await axios.delete(`${API}/trainers/exceptions/${exc.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Deleted exception:', exc.id, exc.start_time);
      }

      if (exceptionsToDelete.length > 0) {
        console.log(
          `Deleted ${exceptionsToDelete.length} exceptions before blocking`
        );
      }

      // THEN: Create the block
      await axios.post(
        `${API}/trainers/${availabilitySlot.trainerId}/blocks`,
        {
          trainer_id: availabilitySlot.trainerId,
          date: availabilitySlot.date,
          start_time: availabilitySlot.timeSlot,
          end_time: endTime,
          reason: (reason && reason.trim()) || 'Manuell blockiert',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        '✅ Slot blockiert! Dieser Zeitraum ist jetzt nicht mehr buchbar.'
      );
      setAvailabilityDialogOpen(false);
      await fetchTrainers();
      await fetchVacationsAndBlocks();
    } catch (error) {
      console.error('Error adding block:', error);
      toast.error('Fehler beim Blockieren des Slots');
    }
  };

  // 06/2026: Trainer GANZTÄGIG sperren (1-Klick).
  const handleAddBlockAllDay = async (reason) => {
    if (!availabilitySlot) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/trainers/${availabilitySlot.trainerId}/blocks`,
        {
          trainer_id: availabilitySlot.trainerId,
          date: availabilitySlot.date,
          all_day: true,
          reason: (reason && reason.trim()) || 'Ganztägig blockiert',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('🚫 Trainer ganztägig gesperrt.');
      setAvailabilityDialogOpen(false);
      await fetchTrainers();
      await fetchVacationsAndBlocks();
    } catch (error) {
      console.error('Error adding all-day trainer block:', error);
      toast.error('Fehler beim ganztägigen Sperren');
    }
  };

  // 06/2026: STUDIOWEITE Sperre anlegen (Zeitfenster oder ganztägig).
  const handleAddStudioBlock = async (endTime, allDay = false, reason) => {
    if (!availabilitySlot) return;
    const studioId =
      studioFilter && studioFilter !== 'all'
        ? studioFilter
        : (availabilitySlot.trainer?.trainer?.studio_ids?.[0] ||
           availabilitySlot.trainer?.studio_ids?.[0] ||
           availabilitySlot.trainer?.trainer?.studio_id ||
           availabilitySlot.trainer?.studio_id ||
           null);
    if (!studioId) {
      toast.error('Bitte zuerst ein Studio oben im Kalender wählen.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const body = allDay
        ? {
            studio_id: studioId,
            date: availabilitySlot.date,
            all_day: true,
            reason: (reason && reason.trim()) || 'Manuell gesperrt',
          }
        : {
            studio_id: studioId,
            date: availabilitySlot.date,
            start_time: availabilitySlot.timeSlot,
            end_time: endTime,
            reason: (reason && reason.trim()) || 'Manuell gesperrt',
          };
      await axios.post(`${API}/studios/${studioId}/blocks`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        allDay
          ? '🏢 Studio ganztägig gesperrt.'
          : '🏢 Studio-Zeitfenster gesperrt.'
      );
      setAvailabilityDialogOpen(false);
      if (fetchStudioBlocks) await fetchStudioBlocks();
    } catch (error) {
      console.error('Error adding studio block:', error);
      toast.error('Fehler beim Sperren des Studios');
    }
  };

  // 06/2026: STUDIOWEITE Sperre aufheben.
  const handleRemoveStudioBlock = async () => {
    const b = availabilitySlot?.studioBlockedBy;
    if (!b) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/studios/${b.studio_id}/blocks/${b.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('✅ Studio-Sperre aufgehoben.');
      setAvailabilityDialogOpen(false);
      if (fetchStudioBlocks) await fetchStudioBlocks();
    } catch (error) {
      console.error('Error removing studio block:', error);
      toast.error('Fehler beim Aufheben der Studio-Sperre');
    }
  };

  return {
    handleSlotClick,
    handleUnavailableSlotClick,
    handleAvailableSlotClick,
    handleAddException,
    handleAddBlock,
    handleAddBlockAllDay,
    handleAddStudioBlock,
    handleRemoveStudioBlock,
    handleRemoveBlock,
    handleRemoveBlockException,
  };
};

export default useSlotClickActions;
